// Import necessary React hooks and icons
import React, { useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { CiSearch } from "react-icons/ci";
import { RiDeleteBinLine } from "react-icons/ri";
import { CiFileOn } from "react-icons/ci";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import LoadingOverlay from "../../../shared/ui/LoadingOverlay";

// Props that this modal expects from the parent
interface Props {
  isOpen: boolean; // whether modal is open or not
  onClose: () => void; // function to close the modal
}

// File type extension to add some additional data like progress and status
interface UploadedFile extends File {
  progress?: number;
  uploaded?: boolean;
}

// The main modal component
const ReportUploadModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]); // all selected files
  const [uploadError, setUploadError] = useState<string | null>(null); // for errors
  const [isRecommendOpen, setIsRecommendOpen] = useState(false); // toggle recommended section
  const [isDragging, setIsDragging] = useState(false); // track if file is being dragged over drop area
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<
    { name: string; logo: string }[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);

  // Handle file selection via input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newFiles: UploadedFile[] = [
      ...uploadedFiles,
      ...files.map((file) => {
        return Object.assign(file, {
          progress: 100,
          uploaded: false,
        });
      }),
    ];

    setUploadedFiles(newFiles);
    setUploadError(null); // clear any error
  };

  // Delete a file from the list by index
  const handleDelete = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const handleSubmit = () => {
    setIsUploading(true); // Show loading screen
    setTimeout(() => {
      setUploadedFiles([]);
      setUploadError(null);
      setIsUploading(false);
      onClose();
    }, 10000);
  };

  // Don’t render anything if the modal is not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-lg w-[500px] max-w-full p-6 relative">
        {/* Close button in the top-right */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-black"
        >
          <AiOutlineClose size={20} />
        </button>

        {/* Modal title */}
        <h2 className="text-xl text-gray-600 mb-4">Create a Company Report</h2>

        {/* Company search input */}
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">
            Search Company
          </label>
          <div className="items-center justify-between border border-gray-200 rounded-xl py-2`">
            <div className="flex items-center ">
              <input
                type="text"
                placeholder="Enter a name to search"
                className="w-full text-gray-400 focus:outline-none focus:ring-0  rounded-xl px-3 py-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <CiSearch className="h-5 w-5 mr-2 text-gray-400" />
            </div>

            {suggestions.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-200 rounded-md shadow mt-3  max-h-60 overflow-y-auto">
                {suggestions.map((company, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      setSearchTerm(company.name);
                      setSuggestions([]);
                    }}
                  >
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="w-6 h-6 rounded-full object-contain"
                    />
                    <span>{company.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* File upload section */}
        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-2">
            Add Financials and Other Files
          </label>

          {/* Uploaded file list */}
          <div className="max-h-60 overflow-y-auto space-y-3 mb-2">
            {uploadedFiles.length > 0 &&
              uploadedFiles.map((file, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-xl p-3 space-y-2"
                >
                  {/* File info and delete button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center">
                        <CiFileOn className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-sm font-medium text-gray-800 break-all">
                          {file.name || "Unnamed file"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>

                    {/* If uploaded, show checkmark, else show delete button */}
                    {file.uploaded ? (
                      <div className="text-green-500">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    ) : (
                      <button
                        className="text-gray-400 hover:text-red-500 transition"
                        onClick={() => handleDelete(index)}
                      >
                        <RiDeleteBinLine className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {/* Progress bar for upload */}
                  <div className="w-full h-1 bg-gray-200 rounded overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-300"
                      style={{ width: `${file.progress ?? 100}%` }}
                    />
                  </div>
                </div>
              ))}
          </div>

          {/* Drag and drop file area */}
          <label htmlFor="file-upload">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const files = Array.from(e.dataTransfer.files);
                const validFiles = files.filter((file) =>
                  [
                    ".pdf",
                    ".docx",
                    ".xlsx",
                    ".jpg",
                    ".png",
                    ".html",
                    ".ppt",
                    ".pptx",
                  ].some((ext) => file.name.toLowerCase().endsWith(ext))
                );
                setUploadedFiles((prev) => [...prev, ...validFiles]);
              }}
              className={`border border-dashed rounded-xl px-6 py-10 cursor-pointer transition text-center ${
                isDragging ? "bg-blue-50 border-blue-500" : "border-gray-300 "
              }`}
            >
              <p className="text-sm text-gray-700">
                Drop your files here or{" "}
                <span className="text-blue-600">browse</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                .csv, .xlsx, .pdf, .docx etc. Up to 50 MB
              </p>
            </div>

            {/* Hidden file input that triggers on click */}
            <input
              id="file-upload"
              type="file"
              accept=".pdf,.docx,.xlsx,.jpg,.png,.html,.ppt,.pptx"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </label>

          {/* Display upload error if there is one */}
          {uploadError && (
            <p className="text-red-500 text-sm mt-2">{uploadError}</p>
          )}
        </div>

        {/* Toggle section for recommendations */}
        <div className="mt-6 mb-2">
          <button
            onClick={() => setIsRecommendOpen(!isRecommendOpen)}
            className="flex items-center text-sm font-medium text-gray-600 focus:outline-none"
          >
            <span>Recommended Uploads</span>
            {isRecommendOpen ? (
              <IoChevronUpOutline className="ml-1 w-4 h-4" />
            ) : (
              <IoChevronDownOutline className="ml-1 w-4 h-4" />
            )}
          </button>

          {/* Recommended uploads message */}
          {isRecommendOpen && (
            <p className="mt-1 text-sm text-gray-500 max-w-xl">
              Upload financial spreadsheets, annual reports, due diligence
              reports, etc. containing key financial and company data to enrich
              insights in the company report
            </p>
          )}
        </div>

        {/* Submit button */}
        <div className="flex items-center justify-end mt-6">
          <button
            className="px-3 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-800 transition cursor-pointer"
            onClick={() => handleSubmit()}
          >
            Create Report
          </button>
        </div>
      </div>

      {/* Loading screen */}
      {isUploading && (
        <LoadingOverlay
          onMinimize={() => {
            setIsUploading(false), onClose();
          }}
        />
      )}
    </div>
  );
};

// Export the modal component
export default ReportUploadModal;
